import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { ProgressSpinner } from 'primereact/progressspinner';
import { TabView, TabPanel } from 'primereact/tabview';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { useGetLmsTrainingReportDetailByIdQuery } from '../../../../api';
import toastService from '../../../../utils/toastService';
import { formatDateForApi, parseLocalDate } from '../../../../utils/dateUtils';
import TrainingReportFormFields from './component/TrainingReportFormFields';
import TrainingReportCostsTab from '../components/TrainingReportCostsTab';
import TrainingReportParticipantsTab from '../components/TrainingReportParticipantsTab';
import TrainingReportInstructorsTab from '../components/TrainingReportInstructorsTab';
import useTrainingReportMutations from './hook/useTrainingReportMutations';
import { useLmsPermission } from '../../hooks/useLmsPermission';
import { LMS } from '../../../../constants/permissionCodes';

const emptyFormData = {
    catalogPlanningId: null,
    country: '',
    planningLocation: null,
    trainingStartDate: null,
    trainingEndDate: null,
    vehicleModel: null,
    trainingType: null,
    givenBy: '',
    month: '',
    week: '',
    status: null,
    numberOfParticipants: null,
    participantType: '',
    detail: '',
    reportType: '',
    numberOfDays: null,
    hour: null,
    numberOfTrainers: null,
    manHour: null,
    estimatedCostEur: null,
    costSpentEur: null,
    estimatedCostUsd: null,
    costSpentUsd: null,
    estimatedCostTry: null,
    costSpentTry: null,
    isActive: 1
};

/**
 * Eğitim Raporu ekleme/düzenleme sayfası. Eskiden bir modal'dı (`TrainingReportForm.jsx`); artık
 * `TrainingCatalogCreateEdit.jsx` ile aynı desende ayrı bir sayfa — üstte form alanları, altta
 * (yalnızca düzenleme modunda, alt satırlar `trainingReportId` gerektirdiği için) Maliyet/Katılımcı/
 * Eğitmen sekmeleri. Route'lar `training-report/create` ve `training-report/:id` (bkz. `App.jsx`).
 * Bkz. docs/LMS-Egitim-Raporlari-API.md §2-6.
 */
const TrainingReportCreateEdit = () => {
    const { th } = useLanguage();
    const { can } = useLmsPermission();
    const subListReadOnly = !can([LMS.REPORT_CREATE, LMS.REPORT_UPDATE]);
    const { id } = useParams();
    const navigate = useNavigate();
    const isEdit = !!id;

    // GetDetailById tek istekte rapor + maliyet/katılımcı/eğitmen sayılarını (sekme rozetleri için)
    // getirir; alt sekmelerin kendi listeleri yine kendi hook'larıyla ayrıca sayfalanır (doc §3.6).
    const { data, isLoading: isLoadingDetail, isFetching: isFetchingDetail } = useGetLmsTrainingReportDetailByIdQuery(id, { skip: !isEdit });
    const { saveReport, deleteReport, isCreating, isUpdating, isDeleting } = useTrainingReportMutations();

    const [formData, setFormData] = useState(emptyFormData);
    const [localSubmitted, setLocalSubmitted] = useState(false);
    const [activeTabIndex, setActiveTabIndex] = useState(0);

    useEffect(() => {
        if (!isEdit) {
            setFormData(emptyFormData);
            setLocalSubmitted(false);
            return;
        }
        if (!data) return;
        setFormData({
            catalogPlanningId: data.catalogPlanningId ?? null,
            country: data.country ?? '',
            planningLocation: data.planningLocation ?? null,
            trainingStartDate: parseLocalDate(data.trainingStartDate),
            trainingEndDate: parseLocalDate(data.trainingEndDate),
            vehicleModel: data.vehicleModel ?? null,
            trainingType: data.trainingType ?? null,
            givenBy: data.givenBy ?? '',
            month: data.month ?? '',
            week: data.week ?? '',
            status: data.status ?? null,
            numberOfParticipants: data.numberOfParticipants ?? null,
            participantType: data.participantType ?? '',
            detail: data.detail ?? '',
            reportType: data.reportType ?? '',
            numberOfDays: data.numberOfDays ?? null,
            hour: data.hour ?? null,
            numberOfTrainers: data.numberOfTrainers ?? null,
            manHour: data.manHour ?? null,
            estimatedCostEur: data.estimatedCostEur ?? null,
            costSpentEur: data.costSpentEur ?? null,
            estimatedCostUsd: data.estimatedCostUsd ?? null,
            costSpentUsd: data.costSpentUsd ?? null,
            estimatedCostTry: data.estimatedCostTry ?? null,
            costSpentTry: data.costSpentTry ?? null,
            isActive: data.isActive !== undefined && data.isActive !== null ? Number(data.isActive) : 1
        });
        setLocalSubmitted(false);
    }, [isEdit, data]);

    const isFieldInvalid = (field) => {
        if (!localSubmitted) return false;
        if (field === 'vehicleModel') return !formData.catalogPlanningId && !formData.vehicleModel;
        if (field === 'trainingType') return !formData.catalogPlanningId && !formData.trainingType;
        return false;
    };

    const handleSave = async () => {
        setLocalSubmitted(true);

        const missingRequired = !formData.catalogPlanningId && (!formData.vehicleModel || !formData.trainingType);
        const invalidRange = formData.trainingStartDate && formData.trainingEndDate && formData.trainingEndDate < formData.trainingStartDate;

        if (missingRequired) {
            toastService.error(th('trainingReport.formValidationMessage'), th('trainingCatalog.missingField'));
            return;
        }
        if (invalidRange) {
            toastService.error(th('trainingReport.dateRangeError'), th('trainingCatalog.missingField'));
            return;
        }

        const payload = {
            catalogPlanningId: formData.catalogPlanningId ?? null,
            country: formData.country.trim() || null,
            planningLocation: formData.planningLocation || null,
            trainingStartDate: formData.trainingStartDate ? formatDateForApi(formData.trainingStartDate) : null,
            trainingEndDate: formData.trainingEndDate ? formatDateForApi(formData.trainingEndDate) : null,
            vehicleModel: formData.vehicleModel || null,
            trainingType: formData.trainingType || null,
            givenBy: formData.givenBy.trim() || null,
            month: formData.month.trim() || null,
            week: formData.week.trim() || null,
            status: formData.status,
            numberOfParticipants: formData.numberOfParticipants,
            participantType: formData.participantType.trim() || null,
            detail: formData.detail.trim() || null,
            reportType: formData.reportType.trim() || null,
            numberOfDays: formData.numberOfDays,
            hour: formData.hour,
            numberOfTrainers: formData.numberOfTrainers,
            manHour: formData.manHour,
            estimatedCostEur: formData.estimatedCostEur,
            costSpentEur: formData.costSpentEur,
            estimatedCostUsd: formData.estimatedCostUsd,
            costSpentUsd: formData.costSpentUsd,
            estimatedCostTry: formData.estimatedCostTry,
            costSpentTry: formData.costSpentTry,
            isActive: Number(formData.isActive)
        };
        if (isEdit) payload.id = Number(id);

        const result = await saveReport(payload, isEdit);
        if (result.success && !isEdit && result.newId) {
            navigate(`/learning-management-system/training-report/${result.newId}`, { replace: true });
        }
    };

    const handleDelete = () => {
        confirmDialog({
            message: th('trainingReport.confirmDeleteMessage'),
            header: th('common.delete'),
            icon: 'pi pi-exclamation-triangle',
            acceptClassName: 'p-button-danger',
            accept: () => deleteReport(id)
        });
    };

    if (isEdit && isLoadingDetail) {
        return (
            <div className="card flex justify-content-center p-5">
                <ProgressSpinner />
            </div>
        );
    }

    return (
        <div className="card">
            <ConfirmDialog />

            <div className="flex align-items-center gap-2 mb-4">
                <Button icon="pi pi-arrow-left" rounded text onClick={() => navigate('/learning-management-system/training-report')} title={th('common.back')} />
                <h1 className="m-0">{isEdit ? th('trainingReport.editTitle') : th('trainingReport.addTitle')}</h1>
            </div>

            <TrainingReportFormFields formData={formData} setFormData={setFormData} isFieldInvalid={isFieldInvalid} />

            <div className="flex justify-content-end gap-2 mt-3">
                {isEdit && can(LMS.REPORT_DELETE) && <Button label={th('common.delete')} icon="pi pi-trash" severity="danger" outlined onClick={handleDelete} loading={isDeleting} />}
                {(!isEdit || can(LMS.REPORT_UPDATE)) && <Button label={th('common.save')} icon="pi pi-check" onClick={handleSave} loading={isCreating || isUpdating || (isEdit && isFetchingDetail)} />}
            </div>

            {isEdit && (
                <>
                    <div className="my-4 border-top-1 border-300" />
                    <TabView activeIndex={activeTabIndex} onTabChange={(e) => setActiveTabIndex(e.index)}>
                        <TabPanel
                            header={
                                <span className="tab-header">
                                    <i className="pi pi-wallet mr-2" />
                                    {th('trainingReport.costs.tabTitle')}
                                    {Number(data?.costs?.length) > 0 && <span className="ml-2 px-2 py-1 border-round text-xs font-semibold bg-blue-100 text-blue-800">{data.costs.length}</span>}
                                </span>
                            }
                        >
                            <TrainingReportCostsTab trainingReportId={data?.id} readOnly={subListReadOnly} />
                        </TabPanel>
                        <TabPanel
                            header={
                                <span className="tab-header">
                                    <i className="pi pi-id-card mr-2" />
                                    {th('trainingReport.participants.tabTitle')}
                                    {Number(data?.participants?.length) > 0 && <span className="ml-2 px-2 py-1 border-round text-xs font-semibold bg-blue-100 text-blue-800">{data.participants.length}</span>}
                                </span>
                            }
                        >
                            <TrainingReportParticipantsTab trainingReportId={data?.id} readOnly={subListReadOnly} />
                        </TabPanel>
                        <TabPanel
                            header={
                                <span className="tab-header">
                                    <i className="pi pi-users mr-2" />
                                    {th('trainingReport.instructors.tabTitle')}
                                    {Number(data?.instructors?.length) > 0 && <span className="ml-2 px-2 py-1 border-round text-xs font-semibold bg-blue-100 text-blue-800">{data.instructors.length}</span>}
                                </span>
                            }
                        >
                            <TrainingReportInstructorsTab trainingReportId={data?.id} readOnly={subListReadOnly} />
                        </TabPanel>
                    </TabView>
                </>
            )}
        </div>
    );
};

export default TrainingReportCreateEdit;
